import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, RefreshCw, Trash2, MessageCircle, Users2, Check, CheckCheck } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const isPrivileged = ["admin", "manager", "branch"].includes(user?.role);
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState("");
  const bottomRef = useRef(null);

  // Compose (privileged only)
  const [composeOpen, setComposeOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [msg, setMsg] = useState("");
  const [recipients, setRecipients] = useState([]);   // user ids
  const [users, setUsers] = useState([]);

  const loadThreads = async () => {
    const { data } = await api.get("/messages/threads");
    setThreads(data);
    if (data.length && !selectedId) setSelectedId(data[0].id);
  };
  const loadDetail = async (tid) => {
    if (!tid) { setDetail(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/messages/threads/${tid}`);
      setDetail(data);
      setThreads((prev) => prev.map((t) => t.id === tid ? { ...t, my_unread: 0 } : t));
    } finally { setLoading(false); }
  };

  useEffect(() => { loadThreads(); if (isPrivileged) api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadDetail(selectedId); /* eslint-disable-next-line */ }, [selectedId]);
  useEffect(() => {
    const iv = setInterval(() => { loadThreads(); if (selectedId) loadDetail(selectedId); }, 20000);
    return () => clearInterval(iv);
    /* eslint-disable-next-line */
  }, [selectedId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [detail?.messages?.length]);

  const sendReply = async () => {
    if (!reply.trim() || !selectedId) return;
    try {
      await api.post(`/messages/threads/${selectedId}/reply`, { body: reply.trim() });
      setReply("");
      await loadDetail(selectedId);
      await loadThreads();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const sendCompose = async () => {
    if (recipients.length === 0) return toast.error("Pick at least one recipient");
    if (!msg.trim()) return toast.error("Write a message first");
    try {
      const { data } = await api.post("/messages/threads", {
        recipient_ids: recipients, subject: subject.trim(), body: msg.trim(),
      });
      toast.success(`Sent to ${data.created} recipient${data.created === 1 ? "" : "s"} (private threads)`);
      setSubject(""); setMsg(""); setRecipients([]); setComposeOpen(false);
      await loadThreads();
      if (data.thread_ids?.[0]) setSelectedId(data.thread_ids[0]);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Send failed"); }
  };

  const deleteThread = async (tid) => {
    if (!window.confirm("Delete this conversation permanently?")) return;
    try {
      await api.delete(`/messages/threads/${tid}`);
      toast.success("Conversation deleted");
      setSelectedId(null); setDetail(null);
      await loadThreads();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const otherName = (t) => {
    const otherId = (t.participants || []).find((p) => p !== user?.id);
    return t.participant_names?.[otherId] || "Staff";
  };

  const totalUnread = useMemo(() => threads.reduce((n, t) => n + (t.my_unread || 0), 0), [threads]);

  return (
    <div className="space-y-4" data-testid="messages-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-[color:var(--brand-teal)]" />
          <div>
            <div className="font-display font-black text-slate-900 text-lg tracking-tight">Messages</div>
            <div className="text-xs text-slate-500">
              {isPrivileged
                ? "Send private messages to individual staff or a group at once. Each recipient gets their own private thread."
                : "Private conversations with your admin or manager."}
              {totalUnread > 0 && <span className="ml-2 text-red-600 font-semibold">· {totalUnread} unread</span>}
            </div>
          </div>
        </div>
        <div className="flex-1" />
        <Button onClick={() => { loadThreads(); if (selectedId) loadDetail(selectedId); }} className="h-9 bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="messages-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
        {isPrivileged && (
          <Button onClick={() => setComposeOpen((v) => !v)} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="compose-btn">
            <Send className="h-4 w-4 mr-1.5" /> Compose
          </Button>
        )}
      </div>

      {composeOpen && isPrivileged && (
        <div className="bg-white border-2 border-emerald-500 rounded-md p-4 space-y-3" data-testid="compose-panel">
          <div className="flex items-center gap-2">
            <Users2 className="h-4 w-4 text-emerald-600" />
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Recipients ({recipients.length} selected)</Label>
          </div>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 bg-slate-50 border border-slate-200 rounded">
            {users.filter((u) => u.id !== user?.id && u.active !== false).map((u) => {
              const on = recipients.includes(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setRecipients((r) => on ? r.filter((x) => x !== u.id) : [...r, u.id])}
                  className={`px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                    on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-300 hover:border-emerald-400"
                  }`}
                  data-testid={`recipient-${u.id}`}
                >
                  {u.name} · {u.role}
                </button>
              );
            })}
            {users.filter((u) => u.id !== user?.id && u.active !== false).length === 0 && (
              <span className="text-xs text-slate-400">No staff available</span>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-wider">Subject (optional)</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Weekly review" className="h-9" data-testid="compose-subject" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-wider">Message</Label>
              <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder="Type your private message…" data-testid="compose-body" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setComposeOpen(false)} className="h-9 bg-slate-200 hover:bg-slate-300 text-slate-800">Cancel</Button>
            <Button onClick={sendCompose} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="send-compose-btn">
              <Send className="h-4 w-4 mr-1.5" /> Send Privately
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-3">
        {/* Threads list */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="threads-list">
          <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-600">Conversations</div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
            {threads.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">No conversations yet</div>}
            {threads.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors ${selectedId === t.id ? "bg-slate-100" : ""}`}
                data-testid={`thread-${t.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-slate-800 text-sm truncate">{otherName(t)}</div>
                  {t.my_unread > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-black">
                      {t.my_unread}
                    </span>
                  )}
                </div>
                {t.subject && <div className="text-[11px] text-slate-500 font-semibold truncate">{t.subject}</div>}
                <div className="text-[11px] text-slate-500 truncate mt-0.5">{t.last_message_preview}</div>
                <div className="text-[10px] text-slate-400 font-mono-num mt-0.5">{(t.last_message_at || "").slice(0, 16).replace("T", " ")}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Thread detail */}
        <div className="bg-white border border-slate-200 rounded-md flex flex-col overflow-hidden" data-testid="thread-detail">
          {!detail ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm p-10">Select a conversation on the left</div>
          ) : (
            <>
              <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900">{otherName(detail.thread)}</div>
                  {detail.thread.subject && <div className="text-[11px] text-slate-500">{detail.thread.subject}</div>}
                </div>
                {isPrivileged && (
                  <button onClick={() => deleteThread(detail.thread.id)} className="p-1 rounded hover:bg-red-50 text-red-600" title="Delete conversation">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50 min-h-[300px] max-h-[60vh]">
                {loading && <div className="text-center text-xs text-slate-400">Loading…</div>}
                {detail.messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-lg px-3 py-2 ${mine ? "bg-emerald-600 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                        {!mine && <div className="text-[10px] font-bold uppercase tracking-wider mb-0.5 opacity-70">{m.sender_name}</div>}
                        <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                        <div className={`text-[10px] mt-1 font-mono-num inline-flex items-center gap-0.5 ${mine ? "text-white/70" : "text-slate-400"}`}>
                          {(m.sent_at || "").slice(11, 16)}
                          {mine && <CheckCheck className="h-3 w-3" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <div className="p-3 border-t border-slate-200 flex items-end gap-2">
                <Textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendReply(); } }}
                  rows={2}
                  placeholder="Type a reply… (Ctrl/Cmd+Enter to send)"
                  className="flex-1 text-sm"
                  data-testid="reply-input"
                />
                <Button onClick={sendReply} disabled={!reply.trim()} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="send-reply-btn">
                  <Send className="h-4 w-4 mr-1.5" /> Reply
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
